import "nprogress/nprogress.css";
import "antd/dist/antd.variable.min.css";
import "styles/root.scss";
import "styles/base.scss";
import "styles/globals.scss";
import "styles/ant.scss";
import React from "react";
import type { AppProps } from "next/app";
import { useRouter } from "next/router";
import Head from "next/head";
import Script from "next/script";
import NProgress from "nprogress";

import { ConfigProvider, message } from "antd";
import { XtLocale, Context, Cookie, Request, Seo, Platform } from "@xt/xtbase";
import { isRtlLocale } from "@/utils/rtl";
const { XtContext, XtProvider } = Context;
import langMessages from "locales/langMessages";
import useWindowResize from "hooks/useWindowResize";
// import "utils/expand";
import store from "store";
import { BreakpointEnum } from "store/app";
const Package: any = require("../../package.json"); // eslint-disable-line
import PWAInstallPrompt from "components/app/PWAInstallPrompt";
import PWAUpdatePrompt from "components/app/PWAUpdatePrompt";
// import CustomerService from "components/app/CustomerService";
import { useServiceWorkerRegistration } from "@/hooks/useServiceWorkerRegistration";
import { getHosts, getZendeskLocale, getOrigin } from "utils/method";
import { trackFbPixelPageView, getFbPixelId } from "@/utils/facebookPixel";
import proxyApi from "../../proxyApi.json";

/** 站点图标 / manifest / favicon 缓存版本：换图后递增，需与 public/manifest.json 中 ?v= 保持一致 */
const APP_ICON_VERSION = "1";
const faviconIco = `/favicon.ico?v=${APP_ICON_VERSION}`;

interface XtContextState {
  theme?: string;
  currency?: string;
}

const Content = ({ theme, children }) => {
  const router = useRouter();
  const [appState, appDispatch] = React.useContext(XtContext);
  
  // 注册Service Worker
  useServiceWorkerRegistration();
  
  React.useEffect(() => {
    //payload
    const payload: XtContextState = {};
    const payloadTheme = theme || "dark";
    if (/^(dark|light)$/.test(payloadTheme)) {
      payload.theme = payloadTheme;
      // Cookie.set("theme", theme);
    }
    const currency = Cookie.get("currency");
    currency && (payload.currency = currency);
    appDispatch({ payload });
    //clientCode
    let clientCode = Cookie.get("clientCode");
    if (!clientCode || clientCode.length !== 32) {
      clientCode = Date.now() + generateNonceStr(19);
      Cookie.set("clientCode", clientCode);
    }
    Request.setHeader({ "client-code": clientCode });
    
    // 配置 axios 请求拦截器，在客户端将相对路径转换为完整 URL
    if (typeof window !== "undefined") {
      const axiosInstance = Request.XtAxios.instance;
      
      // 需要代理的 API 路径前缀（从 proxyApi.json 读取）
      const proxyApiPaths = proxyApi || [];
      
      const requestInterceptor = (config) => {
        // 如果是相对路径且以需要代理的路径开头，转换为完整 URL
        if (config.url && typeof config.url === "string" && config.url.startsWith("/")) {
          const pathMatch = proxyApiPaths.find((path: string) => config.url.startsWith(`/${path}/`));
          if (pathMatch) {
            // 在客户端，始终使用配置的 NEXT_PUBLIC_host 和 NEXT_PUBLIC_protocol
            const host = process.env.NEXT_PUBLIC_host;
            const protocol = process.env.NEXT_PUBLIC_protocol;
            if (host && protocol) {
              config.url = `${protocol}//${host}${config.url}`;
            }
          }
        }
        return config;
      };
      
      // 为 XtAxios 添加请求拦截器（XtAxios4 使用同一个 instance）
      axiosInstance.interceptors.request.use(requestInterceptor);
    }
    
    //antd
    message.config({
      top: 60,
    });
    ConfigProvider.config({
      theme: {
        primaryColor: "#B9F78A",
      },
    });
    // 客服已替换为 LiveHelp100，以下 Zendesk 加载注释保留，后续可能恢复
    /* Zendesk 原逻辑
    if (typeof window !== "undefined" && !Platform.isInApp()) {
      document.body.classList.add("zendesk-hide");
      const pageLocale = router?.locale;
      const script = document.createElement("script");
      script.setAttribute(
        "src",
        "https://static.zdassets.com/ekr/snippet.js?key=8d996cc2-d7cd-4cab-a32d-d714c739f6ce"
      );
      script.setAttribute("async", "true");
      script.setAttribute("id", "ze-snippet");
      script.setAttribute("crossorigin", "anonymous");
      script.onload = function () {
        const zE = (window as any).zE || (window as any).$zE;
        if (typeof zE !== "function") return;
        const zenLocale = getZendeskLocale(pageLocale);
        try {
          zE("webWidget", "setLocale", zenLocale);
          // Messaging 组件 UI 语言（“输入消息”、“刚才”等）用 messenger:set locale，否则会跟浏览器语言走成中文
          zE("messenger:set", "locale", zenLocale);
          // 与 ssr-web-index 一致：初始隐藏，关闭后再次隐藏，避免 widget 一直留在页面上
          zE("webWidget", "hide");
          zE("messenger", "hide");
          zE("webWidget:on", "close", function () {
            try {
              zE("webWidget", "hide");
            } catch (e) {}
          });
          const doMessengerHide = () => {
            try {
              zE("messenger", "hide");
            } catch (err) {}
          };
          zE("messenger:on", "close", function () {
            doMessengerHide();
            [50, 200, 500, 1000, 2000].forEach((ms: number) => setTimeout(doMessengerHide, ms));
          });
        } catch (e) {}
      };
      document.body.appendChild(script);
    }
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 语言切换时同步更新 Zendesk widget 语言（已注释，后续可能恢复）
  // React.useEffect(() => {
  //   if (typeof window === "undefined") return;
  //   const zE = (window as any).zE || (window as any).$zE;
  //   if (typeof zE !== "function") return;
  //   const zenLocale = getZendeskLocale(router.locale);
  //   try {
  //     zE("webWidget", "setLocale", zenLocale);
  //     zE("messenger:set", "locale", zenLocale);
  //   } catch (e) {}
  // }, [router.locale]);

  // AWS CloudWatch RUM（pro / qa 环境上报）
  React.useEffect(() => {
    if (typeof window === "undefined" || !["pro", "qa"].includes(process.env.NEXT_PUBLIC_ENV || "")) return;
    import("@/snippet").then((m) => m.initAwsRum());
  }, []);

  React.useEffect(() => {
    const isLogin = appState.loginStatus === 1;
    const token = isLogin ? Cookie.get("token") : "";

    store.balances.updateState({
      convertCurrency: appState.currency,
    });
    store.user.updateState({
      isLogin,
      token,
      userInfo: appState.userInfo,
      authInfo: appState.authInfo,
    });

    console.log("%c【appState】", "color:#00aecc", appState);
  }, [appState]);
  React.useEffect(() => {
    const handleRouteStart = () => {
      // console.log("NProgress.start()========");
      NProgress.start();
    };
    const handleRouteDone = () => {
      // console.log("NProgress.done()========");
      NProgress.done();
    };

    router.events.on("routeChangeStart", handleRouteStart);
    router.events.on("routeChangeComplete", handleRouteDone);
    router.events.on("routeChangeError", handleRouteDone);

    return () => {
      router.events.off("routeChangeStart", handleRouteStart);
      router.events.off("routeChangeComplete", handleRouteDone);
      router.events.off("routeChangeError", handleRouteDone);
    };
  }, [router.events]);

  React.useEffect(() => {
    if (process.env.NEXT_PUBLIC_LOCAL) {
      if (router.query.token) {
        // console.log("router.query.token----->", router.query.token);
        // console.log("router.pathname----->", router);
        Cookie.set("token", router.query.token as string);
        router.replace("/trade/" + router.query.symbol);
      }
    }
  }, [router]);

  return children;
};

function App({ Component, pageProps }: AppProps) {
  const { locale, locales } = useRouter();
  const isRtl = isRtlLocale(locale);
  XtLocale.init(locale, locales, langMessages);
  const localeObj = XtLocale.getLocaleObj(locale);
  const router = useRouter();
  const seoTitle = pageProps?.tdk?.title || "Spikex";
  const seoDescription = pageProps?.tdk?.describe || "Spikex.COM";
  const seoShareUrl = `${getOrigin("www")}/${locale || "en"}${pageProps?.pageUrl || ""}`;
  // 渠道分享卡片（TG/Twitter/FB）要求绝对图片 URL
  const seoShareImage = `${getOrigin("www")}/logo.png`;

  React.useEffect(() => {
    const dir = isRtl ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("data-dir", dir);
  }, [isRtl]);

  const payload = {
    lang: localeObj.key,
  };
  console.log("%c【App props】", "color:#00aecc", pageProps);

  // const domain = "";
  const domain = !process.env.NEXT_PUBLIC_LOCAL ? process.env.NEXT_PUBLIC_cdn + "/public" : "";
  if (process.env.NEXT_PUBLIC_LOCAL) {
    Request.setHeader({ "X-Forwarded-Host": process.env.NEXT_PUBLIC_host });
  }

  useWindowResize(() => {
    let breakpoint;
    const { innerWidth } = window;
    if (innerWidth >= 1698) {
      breakpoint = BreakpointEnum.xl;
    } else if (innerWidth > 1200) {
      breakpoint = BreakpointEnum.lg;
    } else if (innerWidth > 768) {
      breakpoint = BreakpointEnum.md;
    } else {
      //<= 768
      breakpoint = BreakpointEnum.sm;
    }
    store.app.updateState({ breakpoint, windowInnerWidth: innerWidth });
    document.body.setAttribute("data-breakpoint", breakpoint);
  });
  React.useEffect(() => {
    store.app.updateState({ clientSideReady: true });
    // store.init();
    console.log("%c【App client side ready】", "color:#52c41a", store);
    // Firebase 动态加载（GA 埋点 + Performance 上报），pro / qa 上报
    import("@/utils/firebase").then((m) => m.shouldInitFirebase() && m.initFirebase()).catch(() => { /* ignore */ });
  }, []);

  // Facebook Pixel：路由变化时发送 PageView（带 content_name 便于多项目区分）
  React.useEffect(() => {
    if (typeof window === "undefined" || !getFbPixelId()) return;
    trackFbPixelPageView({ content_name: "trade" });
  }, [router.pathname]);

  return (
    <XtProvider payload={payload} intlValue={XtLocale.intl}>
      <ConfigProvider locale={localeObj.antd} direction={isRtl ? "rtl" : "ltr"} autoInsertSpaceInButton={false}>
        <Head>
          <meta name="version" content={`${Package.version}`} key="version" />
          <meta property="og:url" content={seoShareUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Spikex" />
          <meta property="og:title" content={seoTitle} />
          <meta property="og:description" content={seoDescription} />
          <meta property="og:image" content={seoShareImage} />
          <meta property="og:image:secure_url" content={seoShareImage} />
          <meta property="og:image:alt" content={seoTitle} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@Spikexofficial" />
          <meta name="twitter:url" content={seoShareUrl} />
          <meta name="twitter:title" content={seoTitle} />
          <meta name="twitter:description" content={seoDescription} />
          <meta name="twitter:image" content={seoShareImage} />
          <meta name="twitter:image:src" content={seoShareImage} />
          <meta name="twitter:image:alt" content={seoTitle} />
          <link rel="manifest" href={`/manifest.json?v=${APP_ICON_VERSION}`} />
          <link rel="icon" href={faviconIco} sizes="any" />
          <meta name="theme-color" content="#B9F78A" />
          <meta name="apple-mobile-web-app-capable" content="yes" />
          <meta name="apple-mobile-web-app-status-bar-style" content="default" />
          <meta name="apple-mobile-web-app-title" content="Spikex" />
          <link rel="apple-touch-icon" href={`/icons/icon-180x180.png?v=${APP_ICON_VERSION}`} sizes="180x180" />
          <meta name="mobile-web-app-capable" content="yes" />
          <meta name="msapplication-TileColor" content="#B9F78A" />
          <meta name="msapplication-TileImage" content={`/icons/icon-192x192.png?v=${APP_ICON_VERSION}`} />
        </Head>
        <Seo tdk={pageProps.tdk} lang={pageProps.locale} resolvedUrl={pageProps.pageUrl} Head={Head} />
        {/* Firebase Performance：First Input Delay polyfill，pro / qa 加载 */}
        {["pro", "qa"].includes(process.env.NEXT_PUBLIC_ENV || "") && (
          <Script
            src="https://cdn.jsdelivr.net/npm/first-input-delay@0.1.3/dist/first-input-delay.min.js"
            strategy="beforeInteractive"
          />
        )}
        {/* Meta Facebook Pixel：与 ssr-web-index 同 ID 时用 content_name 区分项目，见 .env 与 utils/facebookPixel */}
        {getFbPixelId() && (
          <>
            <Script
              id="facebook-pixel"
              strategy="afterInteractive"
              dangerouslySetInnerHTML={{
                __html: `
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${getFbPixelId()}');
fbq('track', 'PageView', { content_name: 'trade' }${process.env.NEXT_PUBLIC_FB_PIXEL_TEST_CODE ? `, { test_event_code: '${process.env.NEXT_PUBLIC_FB_PIXEL_TEST_CODE}' }` : ""});
                `,
              }}
            />
            <noscript>
              <img
                height={1}
                width={1}
                style={{ display: "none" }}
                src={`https://www.facebook.com/tr?id=${getFbPixelId()}&ev=PageView&noscript=1`}
                alt=""
              />
            </noscript>
          </>
        )}
        {/* eslint-disable */}
        <Script
          id="app-before-interactive"
          data-theme={pageProps.theme}
          strategy="beforeInteractive"
          src={domain + "/beforeInteractive.js?v=" + Package.version}
        />
        <Script src={domain + "/iconpark.js?v=" + Package.version} />
        {/* eslint-enable */}
        <link rel="stylesheet" type="text/css" href="https://at.alicdn.com/t/font_2502537_ytndssfkiy.css" />
        <Content {...pageProps}>
          <Component {...pageProps} />
          <PWAInstallPrompt />
          <PWAUpdatePrompt />
          {/* <CustomerService /> */}
        </Content>
      </ConfigProvider>
    </XtProvider>
  );
}

export default App;

function generateNonceStr(length: number, type?: string) {
  let chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  type === "num" && (chars = "0123456789");
  const maxPos = chars.length;
  let noceStr = "";
  for (let i = 0; i < (length || 32); i++) {
    noceStr += chars.charAt(Math.floor(Math.random() * maxPos));
  }
  return noceStr;
}
